import React, { useCallback } from "react";
import { Text } from "components/text";
import { useTranslation } from "react-i18next";
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "components/table";
import type { ThemaCode } from "reedy-data/api";
import { ThemaCodesTableRow } from "./ThemaCodesTableRow";
import { ThemaCodesTableSkeletonRows } from "./ThemaCodesTableSkeletonRows";

export const ThemaCodesTable = ({
  data,
  isLoading,
  error,
  query,
  selected,
  onToggle,
  onDrillDown,
  onNavigateToLevel,
  navigationStack,
  type,
  showNotesInline,
  scrollRef,
  renderRowAction,
}: {
  data: ThemaCode[] | undefined;
  isLoading: boolean;
  error: Error | null;
  query: string;
  selected: ThemaCode[];
  onToggle?: (code: ThemaCode) => void;
  onDrillDown: (code: ThemaCode) => void;
  onNavigateToLevel: (item: ThemaCode) => void;
  navigationStack: ThemaCode[];
  type: "subjects" | "qualifiers";
  showNotesInline?: boolean;
  scrollRef?: React.Ref<HTMLDivElement>;
  renderRowAction?: (item: ThemaCode) => React.ReactNode;
}) => {
  const { t } = useTranslation("components");

  const getEmptyMessage = () => {
    if (query) {
      return t("components:themaCodes.noResults");
    }
    if (type === "qualifiers") {
      return t("components:themaCodes.searchHint");
    }
    return t("components:themaCodes.noSubjectsFound");
  };

  const getHeaderText = () => {
    return type === "subjects"
      ? t("components:themaCodes.subjectHeading")
      : t("components:themaCodes.qualifierHeading");
  };

  const getErrorMessage = () => {
    // If we have a search query, it's a search error
    if (query) {
      return t("components:themaCodes.errorSearch");
    }

    // If we're drilling down into a tree, it's a children error
    if (navigationStack.length > 0) {
      return t("components:themaCodes.errorChildren");
    }

    // Otherwise, it's a root categories/qualifiers error
    return type === "subjects"
      ? t("components:themaCodes.errorSubjects")
      : t("components:themaCodes.errorQualifiers");
  };

  // Combine navigation stack with current data to show tree structure
  const getDisplayData = () => {
    if (query || navigationStack.length === 0) {
      return data || [];
    }

    // Show parent items in tree structure
    const treeData: ThemaCode[] = [];

    // Add all parent items from navigation stack with correct levels
    navigationStack.forEach((parent, index) => {
      treeData.push({
        ...parent,
        level: index,
      });
    });

    // Add current level children (excluding any that might already be in navigation stack)
    if (data) {
      const navigationIds = new Set(navigationStack.map((item) => item.id));
      data.forEach((item) => {
        if (!navigationIds.has(item.id)) {
          treeData.push({
            ...item,
            level: navigationStack.length,
          });
        }
      });
    }

    return treeData;
  };

  const handleItemClick = useCallback(
    (item: ThemaCode) => {
      // Check if this item is in the navigation stack (is a parent)
      const itemIndexInStack = navigationStack.findIndex(
        (parent) => parent.id === item.id,
      );

      if (itemIndexInStack >= 0) {
        // Item is a parent in the stack - navigate back to this level
        onNavigateToLevel(item);
      } else {
        // Item is not in stack - drill down normally
        onDrillDown(item);
      }
    },
    [navigationStack, onNavigateToLevel, onDrillDown],
  );

  const displayData = getDisplayData();

  // Search results are flattened
  const isFlatLayout = !!query.trim();

  // Calculate dynamic width for notation column based on max level
  const maxLevel = Math.max(
    0,
    ...displayData.map((item) => item.level ?? 0),
    navigationStack.length,
  );

  // Base width (96px) + additional width for each level (16px per level + buffer)
  const notationColumnWidth = Math.max(96, 96 + maxLevel * 20);

  return (
    <div className="flex-1 flex flex-col border border-secondary-200 rounded-md overflow-hidden">
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        <table className="w-full caption-bottom text-sm table-fixed">
          <TableHeader className="sticky top-0 z-10 bg-secondary-50">
            <TableRow>
              <TableHead
                className="text-sm font-medium text-secondary-700"
                style={{ width: `${notationColumnWidth}px` }}
              >
                {t("components:themaCodes.notation")}
              </TableHead>
              <TableHead className="text-sm font-medium text-secondary-700">
                {getHeaderText()}
              </TableHead>
              <TableHead className="w-16 pr-4" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && navigationStack.length === 0 && (
              <ThemaCodesTableSkeletonRows
                level={0}
                notationColumnWidth={notationColumnWidth}
                flatLayout={isFlatLayout}
              />
            )}

            {error && (
              <TableRow>
                <TableCell colSpan={3} className="h-32 text-center">
                  <Text variant="error">{getErrorMessage()}</Text>
                </TableCell>
              </TableRow>
            )}

            {!error &&
              displayData.map((item: ThemaCode) => {
                const isSelected = selected?.some((s) => s.id === item.id);
                const isParentInStack = navigationStack.some(
                  (p) => p.id === item.id,
                );

                return (
                  <ThemaCodesTableRow
                    key={`${item.id}-level-${item.level ?? 0}`}
                    item={item}
                    selected={isSelected}
                    onToggle={onToggle ? () => onToggle(item) : undefined}
                    onDrillDown={() => handleItemClick(item)}
                    isExpanded={isParentInStack}
                    showNotesInline={showNotesInline}
                    flatLayout={isFlatLayout}
                    renderAction={renderRowAction}
                  />
                );
              })}

            {/* Show skeleton rows only for children being loaded */}
            {isLoading && navigationStack.length > 0 && (
              <ThemaCodesTableSkeletonRows
                level={navigationStack.length}
                notationColumnWidth={notationColumnWidth}
                rowCount={4}
                flatLayout={isFlatLayout}
              />
            )}

            {!isLoading && !error && displayData.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} className="h-32 text-center">
                  <Text variant="muted">{getEmptyMessage()}</Text>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </table>
      </div>
    </div>
  );
};
